// Linter Python: règles heuristiques + parse AST via Pyodide
import { linter, type Diagnostic } from '@codemirror/lint';
import type { Extension } from '@codemirror/state';
// adapte le chemin selon ton arborescence :
import { getPyodide } from '../adapters/python-pyodide/pyodideLoader';

type MultiLineStringState = null | 'single' | 'double';

interface PythonLineAnalysis {
    commentStart: number;
    stringRanges: Array<{ from: number; to: number }>;
    nextState: MultiLineStringState;
}

function analyzePythonLine(lineText: string, initialState: MultiLineStringState): PythonLineAnalysis {
    const stringRanges: Array<{ from: number; to: number }> = [];
    let state: MultiLineStringState = initialState;
    let i = 0;

    const recordRange = (from: number, to: number) => {
        stringRanges.push({ from, to });
    };

    if (state) {
        const triple = state === 'single' ? "'''" : '"""';
        const start = 0;
        while (i < lineText.length) {
            if (lineText.startsWith(triple, i)) {
                i += 3;
                recordRange(start, i);
                state = null;
                break;
            }
            i++;
        }
        if (state) {
            recordRange(start, lineText.length);
            return { commentStart: -1, stringRanges, nextState: state };
        }
    }

    let commentStart = -1;
    while (i < lineText.length) {
        const ch = lineText[i];
        if (ch === '#') {
            commentStart = i;
            break;
        }

        if (ch === '\'' || ch === '"') {
            const quote = ch;
            const isTriple = i + 2 < lineText.length && lineText[i + 1] === quote && lineText[i + 2] === quote;
            const start = i;
            if (isTriple) {
                i += 3;
                const triple = quote === '\'' ? "'''" : '"""';
                const closingIndex = lineText.indexOf(triple, i);
                if (closingIndex === -1) {
                    recordRange(start, lineText.length);
                    state = quote === '\'' ? 'single' : 'double';
                    return { commentStart, stringRanges, nextState: state };
                }
                i = closingIndex + 3;
                recordRange(start, i);
                continue;
            }

            i++;
            while (i < lineText.length) {
                if (lineText[i] === '\\') {
                    i += 2;
                    continue;
                }
                if (lineText[i] === quote) {
                    i++;
                    break;
                }
                i++;
            }
            recordRange(start, Math.min(i, lineText.length));
            continue;
        }

        i++;
    }

    return { commentStart, stringRanges, nextState: state };
}

function isInStringOrComment(index: number, analysis: PythonLineAnalysis): boolean {
    if (analysis.commentStart !== -1 && index >= analysis.commentStart) {
        return true;
    }
    return analysis.stringRanges.some(({ from, to }) => index >= from && index < to);
}

function jsLikeWarnings(text: string, lineNo1: number, lineText: string, lineFrom: number, multiLineState: MultiLineStringState): { diagnostics: Diagnostic[]; nextState: MultiLineStringState } {
    const out: Diagnostic[] = [];
    const analysis = analyzePythonLine(lineText, multiLineState);
    const shouldIgnore = (index: number) => isInStringOrComment(index, analysis);
    const push = (fromRel: number, len: number, message: string, severity: 'warning' | 'error' = 'warning') => {
        out.push({ from: lineFrom + fromRel, to: lineFrom + fromRel + Math.max(1, len), message, severity });
    };

    // // comment → Python veut "#"
    const idxComment = lineText.indexOf('//');
    if (idxComment !== -1 && !shouldIgnore(idxComment)) {
        push(idxComment, 2, 'En Python, utilisez "#" pour les commentaires (// est JavaScript).', 'error');
    }

    // ; en fin de ligne → inutile (toléré par Python, mais on avertit)
    const semiMatch = lineText.match(/;+\s*$/);
    if (semiMatch) {
        const idx = lineText.lastIndexOf(';');
        if (idx !== -1 && !shouldIgnore(idx)) {
            push(idx, 1, 'Le point-virgule est inutile en Python.');
        }
    }

    // console.log → n’existe pas en Python
    const consoleLog = lineText.match(/\bconsole\s*\.\s*log\b/);
    if (consoleLog) {
        const idx = consoleLog.index ?? 0;
        if (!shouldIgnore(idx)) {
            push(idx, 'console.log'.length, 'console.log n’existe pas en Python. Utilisez print(...).', 'error');
        }
    }

    // let / const / var
    const decl = lineText.match(/\b(let|const|var)\b/);
    if (decl) {
        const idx = decl.index ?? 0;
        if (!shouldIgnore(idx)) {
            push(idx, decl[0].length, `Mot-clé "${decl[0]}" (JS). En Python, on assigne directement : x = 1.`, 'error');
        }
    }

    // function (...) { ... }
    const fnKw = lineText.match(/\bfunction\b/);
    if (fnKw) {
        const idx = fnKw.index ?? 0;
        if (!shouldIgnore(idx)) {
            push(idx, fnKw[0].length, `Mot-clé "function" (JS). En Python : "def nom(...):".`, 'error');
        }
    }

    // && / || / ! / === / !==
    const opJS = lineText.match(/(===|!==|&&|\|\||!)(?!=)/);
    if (opJS) {
        const idx = opJS.index ?? 0;
        const map: Record<string, string> = {
            '&&': 'and',
            '||': 'or',
            '!': 'not',
            '===': '==',
            '!==': '!='
        };
        const sug = map[opJS[1]] ?? 'opérateur Python équivalent';
        if (!shouldIgnore(idx)) {
            push(idx, opJS[0].length, `Opérateur JS "${opJS[0]}". En Python utilisez "${sug}".`, 'error');
        }
    }

    return { diagnostics: out, nextState: analysis.nextState };
}

export function pythonLinterExtension(delayMs = 450): Extension {
    return linter(async (view) => {
        const doc = view.state.doc;
        const text = doc.toString();

        const diags: Diagnostic[] = [];

        // Règles “JS-like” ligne par ligne (immédiates)
        const lines = text.split('\n');
        let multiLineState: MultiLineStringState = null;
        for (let i = 0; i < lines.length; i++) {
            const line = doc.line(i + 1);
            const res = jsLikeWarnings(text, i + 1, line.text, line.from, multiLineState);
            diags.push(...res.diagnostics);
            multiLineState = res.nextState;
        }

        // Vérif de syntaxe Python via ast.parse (Pyodide)
        try {
            const pyodide = await getPyodide();
            // on évite les soucis d’échappement en passant par globals
            pyodide.globals.set('__CM_SRC__', text);
            await pyodide.runPythonAsync(`
import ast
_src = __CM_SRC__
try:
    ast.parse(_src)
    __CM_ERR__ = None
except SyntaxError as e:
    __CM_ERR__ = (e.lineno or 1, e.offset or 1, (e.msg or str(e)), e.text or "")
del _src
`);

            const res = pyodide.globals.get('__CM_ERR__');
            if (res && typeof (res as any).toJs === 'function') {
                const [lineno, offset, msg, badline] = (res as any).toJs();
                const ln = Math.max(1, Number(lineno) || 1);
                const off = Math.max(1, Number(offset) || 1);
                const line = doc.line(ln);
                const from = Math.min(line.to, line.from + off - 1);
                const to = Math.min(line.to, from + (badline?.trim()?.length ? badline.trim().length : 1));
                diags.push({
                    from,
                    to: Math.max(from + 1, to),
                    severity: 'error',
                    message: String(msg || 'Erreur de syntaxe Python'),
                });
            }
            try { pyodide.globals.delete('__CM_SRC__'); } catch { }
            try { pyodide.globals.delete('__CM_ERR__'); } catch { }
        } catch {
            // Pyodide pas prêt? On ignore la partie AST (les heuristiques restent).
        }

        return diags;
    }, { delay: delayMs });
}